import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Iconify from 'src/components/Iconify';
import { CHATS_ARQUIVADAS_DESIGN as D, CORES_DIVISAO, getCorDivisao } from './ChatsArquivadas.design';

const ICONES = [
    'eva:folder-outline',
    'eva:book-open-outline',
    'eva:headphones-outline',
    'eva:refresh-outline',
    'eva:credit-card-outline',
    'eva:briefcase-outline',
    'eva:star-outline',
    'eva:flag-outline'
];

export default function ModalDivisao({ aberto, divisao, setores, onFechar, onSalvar }) {
    const { t } = useTranslation('chat');
    const [nome, setNome] = useState('');
    const [corHex, setCorHex] = useState('violet');
    const [icone, setIcone] = useState('eva:folder-outline');
    const [setoresSelecionados, setSetoresSelecionados] = useState([]);
    const [configDesarquivar, setConfigDesarquivar] = useState('herdar');
    const [salvando, setSalvando] = useState(false);
    const [erro, setErro] = useState(null);

    useEffect(() => {
        if (!aberto) return;
        setNome(divisao?.nome || '');
        setCorHex(divisao?.cor_hex || 'violet');
        setIcone(divisao?.icone || 'eva:folder-outline');
        setSetoresSelecionados((divisao?.setores || []).map((s) => `${s}`));
        setConfigDesarquivar(divisao?.config_desarquivar || 'herdar');
        setErro(null);
    }, [aberto, divisao]);

    if (!aberto) return null;

    const toggleSetor = (setorId) => {
        setSetoresSelecionados((atual) =>
            atual.includes(setorId) ? atual.filter((id) => id !== setorId) : [...atual, setorId]
        );
    };

    const handleSalvar = async () => {
        if (!nome.trim()) {
            setErro(t('archive.division.errorNameRequired', 'Informe o nome da divisão'));
            return;
        }

        setSalvando(true);
        setErro(null);
        try {
            await onSalvar({
                nome: nome.trim(),
                cor_hex: corHex,
                icone,
                setores: setoresSelecionados,
                config_desarquivar: configDesarquivar
            });
            onFechar();
        } catch (error) {
            setErro(error?.mensagem || t('archive.division.errorSave', 'Não foi possível salvar a divisão'));
        } finally {
            setSalvando(false);
        }
    };

    const cor = getCorDivisao(corHex);

    const MODOS_DESARQUIVAR = [
        { value: 'herdar', label: t('archive.division.modeInherit', 'Usar padrão da organização') },
        { value: 'auto', label: t('archive.division.modeAuto', 'Desarquivar ao receber mensagem') },
        { value: 'manual', label: t('archive.division.modeManual', 'Manter arquivada mesmo com nova mensagem') }
    ];

    return (
        <div className={D.modal.backdrop} onClick={onFechar}>
            <div className={D.modal.container} onClick={(e) => e.stopPropagation()}>
                <div className={D.modal.header}>
                    <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${cor.bg}`}>
                        <Iconify icon={icone} width={18} className={cor.text} />
                    </div>
                    <div>
                        <p className={D.modal.title}>
                            {divisao ? t('archive.division.editTitle', 'Editar divisão') : t('archive.division.createTitle', 'Nova divisão')}
                        </p>
                        <p className={D.modal.subtitle}>
                            {t('archive.division.subtitle', 'Divisões são os estágios do seu arquivo de conversas')}
                        </p>
                    </div>
                </div>

                <div className={D.modal.body}>
                    {erro && (
                        <div className={D.modal.error}>
                            <Iconify icon="eva:alert-circle-outline" width={14} />
                            {erro}
                        </div>
                    )}

                    <div>
                        <label className={D.modal.label}>{t('archive.division.name', 'Nome')}</label>
                        <input
                            type="text"
                            value={nome}
                            onChange={(e) => setNome(e.target.value)}
                            placeholder={t('archive.division.namePlaceholder', 'Ex: Matrículas')}
                            className={D.modal.input}
                        />
                    </div>

                    <div>
                        <label className={D.modal.label}>{t('archive.division.color', 'Cor')}</label>
                        <div className="flex items-center gap-2">
                            {Object.keys(CORES_DIVISAO).map((chave) => (
                                <button
                                    key={chave}
                                    type="button"
                                    onClick={() => setCorHex(chave)}
                                    className={`${D.modal.colorDot} ${CORES_DIVISAO[chave].bg} ${
                                        corHex === chave ? CORES_DIVISAO[chave].border : 'border-transparent'
                                    }`}
                                >
                                    <span className={`block w-2.5 h-2.5 rounded-full mx-auto ${CORES_DIVISAO[chave].dot}`} />
                                </button>
                            ))}
                        </div>
                    </div>

                    <div>
                        <label className={D.modal.label}>{t('archive.division.icon', 'Ícone')}</label>
                        <div className="flex flex-wrap items-center gap-2">
                            {ICONES.map((opcao) => (
                                <button
                                    key={opcao}
                                    type="button"
                                    onClick={() => setIcone(opcao)}
                                    className={`w-9 h-9 rounded-xl border-2 flex items-center justify-center transition-all ${
                                        icone === opcao
                                            ? 'border-[#7C3AED] bg-violet-50 text-[#7C3AED]'
                                            : 'border-gray-200 text-gray-400 hover:border-gray-300'
                                    }`}
                                >
                                    <Iconify icon={opcao} width={16} />
                                </button>
                            ))}
                        </div>
                    </div>

                    <div>
                        <label className={D.modal.label}>{t('archive.division.unarchiveMode', 'Desarquivamento')}</label>
                        <select
                            value={configDesarquivar}
                            onChange={(e) => setConfigDesarquivar(e.target.value)}
                            className={D.modal.input}
                        >
                            {MODOS_DESARQUIVAR.map((modo) => (
                                <option key={modo.value} value={modo.value}>
                                    {modo.label}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <label className={D.modal.label}>{t('archive.division.sectors', 'Visível para os setores')}</label>
                        {!setores?.length ? (
                            <p className="text-xs text-gray-400">{t('archive.division.noSectors', 'Nenhum setor cadastrado')}</p>
                        ) : (
                            <div className="flex flex-wrap gap-2">
                                {setores.map((setor) => {
                                    const setorId = `${setor._id}`;
                                    const ativo = setoresSelecionados.includes(setorId);
                                    return (
                                        <button
                                            key={setorId}
                                            type="button"
                                            onClick={() => toggleSetor(setorId)}
                                            className={`px-2.5 py-1.5 rounded-lg text-xs font-medium border transition-all ${
                                                ativo
                                                    ? 'bg-violet-50 border-[#7C3AED] text-[#7C3AED]'
                                                    : 'bg-white border-gray-200 text-gray-500 hover:border-gray-300'
                                            }`}
                                        >
                                            {setor.nome_setor}
                                        </button>
                                    );
                                })}
                            </div>
                        )}
                        <p className="text-[11px] text-gray-400 mt-2">
                            {t('archive.division.sectorsHint', 'Sem setor selecionado, a divisão fica visível para todos.')}
                        </p>
                    </div>
                </div>

                <div className={D.modal.footer}>
                    <button type="button" className={D.modal.cancel} onClick={onFechar}>
                        {t('archive.division.cancel', 'Cancelar')}
                    </button>
                    <button
                        type="button"
                        className={D.modal.confirm}
                        onClick={handleSalvar}
                        disabled={salvando}
                    >
                        {salvando ? t('archive.division.saving', 'Salvando...') : t('archive.division.save', 'Salvar')}
                    </button>
                </div>
            </div>
        </div>
    );
}
